import {
    CustomContentProps,
    SnackbarProvider as NotistackSnackbarProvider,
    SnackbarContent,
    SnackbarKey,
} from 'notistack';
import { Alert, AlertProps } from '@mui/material';
import { ReactNode, forwardRef } from 'react';

import CheckCircleIcon from '../Icons/CheckCircleIcon';
import AlertExclamationCircleIcon from '../Icons/AlertExclamationCircleIcon';
import WarningExclamationIcon from '../Icons/WarningExclamationIcon';
import InformationCircleIcon from '../Icons/InformationCircleIcon';

import SnackbarCloseButton from 'modules/common/components/SnackbarCloseButton';

/**
 * Default action button for the snackbar.
 *
 * The button itself is `SnackbarCloseButton`, shared with the toasts that pass an `action` of their
 * own — passing one replaces this default, so they render it back rather than a second copy of it.
 */
function DefaultActionButton(key: SnackbarKey) {
    return <SnackbarCloseButton snackbarKey={key} />;
}

interface CustomAlertProps extends CustomContentProps {
    disableAction?: boolean;
    alertProps?: AlertProps;
    customIcon?: ReactNode;
}

/**
 * Custom component for the snackbar using MUI Alert
 */
const CustomAlert = forwardRef<HTMLDivElement, CustomAlertProps>(
    ({ id, message, variant, action, iconVariant, hideIconVariant, disableAction, customIcon, alertProps }, ref) => {
        const AlertAction = typeof action === 'function' ? action(id) : action;
        const Icon = customIcon || iconVariant[variant];

        return (
            <SnackbarContent ref={ref} role="alert">
                <Alert
                    variant="filled"
                    severity={variant === 'default' ? 'secondary' : variant}
                    icon={hideIconVariant ? false : Icon}
                    action={!disableAction ? AlertAction : null}
                    {...alertProps}>
                    {message}
                </Alert>
            </SnackbarContent>
        );
    }
);

export default function SnackbarProvider({ children, ...props }: { children: ReactNode }) {
    return (
        <NotistackSnackbarProvider
            autoHideDuration={3000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            Components={{
                default: CustomAlert,
                success: CustomAlert,
                error: CustomAlert,
                warning: CustomAlert,
                info: CustomAlert,
                primary: CustomAlert,
            }}
            iconVariant={{
                success: <CheckCircleIcon />,
                error: <AlertExclamationCircleIcon />,
                warning: <WarningExclamationIcon />,
                info: <InformationCircleIcon />,
            }}
            action={DefaultActionButton}
            {...props}>
            {children}
        </NotistackSnackbarProvider>
    );
}
